import {
  useArtifactCanvas,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PersonListCard,
  PaneResponsiveCardGrid,
  DashboardSectionSkeleton,
  Button,
  formatClientDateTimeMedium,
  interviewerSessionHref,
  employerRecentApplicantToPersonModel,
  MatchQualityBadge,
  getHrefWithThreadPreservation,
} from '@yeshuman/sdk'
import { useNavigate } from 'react-router-dom'
import { CalendarPlus, Video } from 'lucide-react'

import { ScheduleInterviewCanvas } from './schedule-interview-canvas'
import { useInterviewerCandidates } from '../../hooks/use-interviewer-candidates'

export function InterviewerCandidatesPage() {
  const navigate = useNavigate()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const { data: candidates = [], isLoading, error } = useInterviewerCandidates()

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title="Candidates"
        description="People in the interview stage. Interview now, or book a time and find it under Interviews."
      />

      {isLoading ? (
        <DashboardSectionSkeleton cards={4} grid />
      ) : error ? (
        <p className="text-sm text-muted-foreground">Unable to load candidates.</p>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No one is waiting to be interviewed.</p>
      ) : (
        <PaneResponsiveCardGrid>
          {candidates.map((row) => {
            const person = employerRecentApplicantToPersonModel(row)
            const interviewHref = getHrefWithThreadPreservation(
              interviewerSessionHref({
                profileId: row.profile.id,
                opportunityId: row.opportunity_id,
                label: person.name,
                interviewId: row.next_interview?.id,
                liveSessionId: row.live_session_id,
              }),
            )
            return (
              <PersonListCard
                key={row.id}
                variant="compact"
                person={person}
                meta={
                  row.next_interview
                    ? formatClientDateTimeMedium(row.next_interview.scheduled_start)
                    : row.applied_at
                      ? `Applied ${new Date(row.applied_at).toLocaleDateString()}`
                      : undefined
                }
                headerAside={
                  typeof row.match_score === 'number' ? (
                    <MatchQualityBadge score={row.match_score} className="shrink-0" />
                  ) : null
                }
                footer={
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" onClick={() => navigate(interviewHref)}>
                      <Video className="size-3.5" aria-hidden />
                      Interview now
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        openCanvas({
                          title: `Schedule · ${person.name}`,
                          content: (
                            <ScheduleInterviewCanvas
                              applicationId={row.id}
                              candidateName={person.name}
                              onClose={closeCanvas}
                            />
                          ),
                        })
                      }
                    >
                      <CalendarPlus className="size-3.5" aria-hidden />
                      Schedule
                    </Button>
                    {row.evaluation_id ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghostMuted"
                        onClick={() => {
                          navigate(
                            getHrefWithThreadPreservation(
                              `/interviewer/evaluations/${row.evaluation_id}`,
                            ),
                            {
                              state: {
                                label: person.name,
                                backPath: '/interviewer/candidates',
                                backLabel: 'Candidates',
                              },
                            },
                          )
                        }}
                      >
                        View evaluation
                      </Button>
                    ) : null}
                  </div>
                }
              />
            )
          })}
        </PaneResponsiveCardGrid>
      )}
    </PageContainer>
  )
}
